"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import ImageWithLoader from "./ImageWithLoader";
// import { useGetProductDetails } from "@/hooks/ApiHooks/productHooks/getProductDetailsHook";
import { useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import Loader from "./Loader";
import { useGetNewProductDetails } from "@/hooks/ApiHooks/newProductListingHooks/newGetProductDetailsHook";

interface DetailsSlidePopoverProps {
  close: () => void;
  productId: string | undefined;
}

export default function DetailsSlidePopover({
  productId,
  close,
}: DetailsSlidePopoverProps) {
  const router = useRouter();

  // const { data: productDetails, isLoading } = useGetProductDetails(productId);
  const { data: productDetails, isLoading } = useGetNewProductDetails(productId || "");
  // const [processedShortDescription, setProcessedShortDescription] = useState(productDetails?.short_description);

  // const productCategoryLength = productDetails?.categories?.length || 0;
  useEffect(() => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = productDetails?.short_description;

    const paragraphs = tempDiv.querySelectorAll("p");
    paragraphs.forEach((p) => {
      if (p.querySelector("strong")) {
        p.classList.add(
          "uppercase",
          "mt-6",
          "mb-1",
          "text-mob-18",
          "sm:text-tab-18",
          "lg:text-web-20",
          "leading-[120%]",
          "font-medium"
        ); // Use mt-6 for 1.5rem (24px) of top margin' leading-[120%] font-medium); // Use mt-6 for 1.5rem (24px) of top margin
      } else {
        p.classList.add("mb-3");
      }
    });

    const anchorTags = tempDiv.querySelectorAll("a");
    anchorTags.forEach((a) => {
      // You might also want to add some padding and rounded corners
      a.classList.add(
        "inline-block",
        "mt-3",
        "mb-3",
        "rounded",
        "py-mob-10",
        "sm:py-tab-10",
        "lg:py-web-12",
        "text-mob-15",
        "sm:text-tab-15",
        "lg:text-web-15",
        "leading-[100%]",
        "font-medium",
        "text-PrimaryLightBlue",
        "border",
        "border-PrimaryLightBlue",
        "rounded-mob-6",
        "sm:rounded-tab-6",
        "lg:rounded-web-6",
        "uppercase",
        "tracking-[0.69vw]",
        "sm:tracking-[0.39vw]",
        "lg:tracking-[0.156vw]",
        "cursor-pointer",
        "w-fit",
        "px-mob-22",
        "sm:px-tab-30",
        "lg:px-web-30",
        "text-PrimaryLightBlue"
      );
    });

    // Set the processed HTML
    // setProcessedShortDescription(tempDiv.innerHTML);
  }, [productDetails?.short_description]);

  const [isVisible, setIsVisible] = useState(true);

  const handleClose = () => {
    setIsVisible(false); // Triggers exit animation
    setTimeout(() => close(), 400); // Delay unmount until animation completes
  };

  return (
    <div>
      <div
        className="fixed w-full h-full left-0 top-0 bg-black/70 z-[148]"
        onClick={close}
      ></div>

      <AnimatePresence>
        {isVisible && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed w-full h-full left-0 top-0 bg-black/70 z-[148]"
              onClick={handleClose}
            />

            <motion.div
              key="popover"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.4 }}
              className="fixed z-[150] top-0 right-0 h-full bg-white sm:w-[65vw] lg:w-[26.04vw] flex flex-col pt-mob-20 sm:pt-tab-30 lg:pt-web-30"
            >
              {/* Close button */}
              <div
                className="px-mob-23 sm:px-tab-23 lg:px-web-23"
                onClick={handleClose}
              >
                <div className="w-mob-59 sm:w-tab-59 lg:w-web-59 cursor-pointer">
                  <Image
                    src="/images/close-btn.svg"
                    alt="icon"
                    fill
                    className="!static !h-full object-cover w-full object-center"
                  />
                </div>
              </div>

              {/* Loading State */}
              {isLoading ? (
                <div className="flex flex-col items-center justify-center h-full px-mob-23 sm:px-tab-23 lg:px-web-23">
                  {/* Spinner */}
                  {/* <div className="relative w-mob-80 sm:w-tab-80 lg:w-web-80 h-mob-80 sm:h-tab-80 lg:h-web-80 mb-mob-20 sm:mb-tab-20 lg:mb-web-20">
                    <div className="absolute inset-0 border-4 border-gray-200 rounded-full"></div>
                    <div className="absolute inset-0 border-4 border-PrimaryLightBlue border-t-transparent rounded-full animate-spin"></div>
                  </div>
                  <p className="text-mob-16 sm:text-tab-16 lg:text-web-18 text-[#323232] font-medium">
                    Loading product details...
                  </p> */}
                  <Loader />
                </div>
              ) : (
                <>
                  {/* product image */}
                  <div className="h-[40.76vw] sm:h-[33.46vw] lg:h-[13.38vw] relative border mb-mob-41 sm:mb-tab-41 lg:mb-web-41">
                    <ImageWithLoader
                      src={
                        productDetails?.result?.images
                      }
                      alt="Product image"
                      fill
                      containerClassName="h-full w-full"
                      className="!static !h-full object-contain w-full object-center"
                    />
                  </div>
                  {/* product short details */}
                  <div className="flex-1 min-h-0 overflow-y-auto mb-mob-20 sm:mb-tab-20 lg:mb-web-20 px-mob-23 sm:px-tab-23 lg:px-web-23">
                    <h2 className="text-mob-28 sm:text-tab-28 lg:text-web-28 text-[#323232] font-medium leading-[120%] mb-mob-5 sm:mb-tab-5 lg:mb-web-5">
                      {productDetails?.result?.post_title || "Product details"}
                    </h2>
                    <div className="flex flex-col gap-mob-28 sm:gap-tab-28 lg:gap-web-28 text-[#1B1B1B]">
                      <div>
                        <h6 className="text-mob-22 sm:text-tab-22 lg:text-web-22 font-medium leading-[140%] mb-mob-2 sm:mb-tab-2 lg:mb-web-2">
                          Description
                        </h6>
                        <div className="leading-[140%] text-mob-20 sm:text-tab-20 lg:text-web-20">
                          {productDetails?.result?.post_excerpt ? (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: productDetails?.result?.post_excerpt
                                  .replace(/_x000D_/g, "")
                                  ?.replace(/<strong>Description<\/strong>/gi, "")
                                  ?.trim(),
                              }}
                            />
                          ) : (
                            <p>No description available.</p>
                          )}
                        </div>
                      </div>
                      {/* <div>
              <h6 className="text-mob-22 sm:text-tab-22 lg:text-web-22 font-medium leading-[140%] mb-mob-2 sm:mb-tab-2 lg:mb-web-2">
                Fields of Application
              </h6>
              <p className="leading-[140%] text-mob-20 sm:text-tab-20 lg:text-web-20">
                Knauf Fire Resistant Gypsum Boards (FR) are high performance
                gypsum boards used as the cladding component of drywall
                constructions such as:
                <ul className="pl-mob-25 sm:pl-tab-25 lg:pl-web-25">
                  <li className="list-disc">
                    Fire rated partitions using metal frames
                  </li>
                  <li className="list-disc">
                    Fire rated suspended ceilings using metal frames
                  </li>
                  <li className="list-disc">
                    Fire rated furring and wall linings
                  </li>
                  <li className="list-disc">Fire rated shaftwalls</li>
                </ul>
              </p>
            </div> */}
                      {/* <div className="flex items-start gap-mob-10 sm:gap-tab-10 lg:gap-web-10"> */}
                      {/* <h6 className="text-mob-16 sm:text-tab-16 lg:text-web-16 leading-[140%] font-medium text-[#1B1B1B]">
                      Categories:
                    </h6> */}

                      {/* <ul className="flex items-center flex-wrap gap-mob-6 sm:gap-tab-6 lg:gap-web-6"> */}
                      {/* {productDetails
                        ? productDetails?.result?.categories.map(
                            (
                              category: { name: string; id: number },
                              index: number
                            ) => (
                              <li
                                key={category.id}
                                className="text-mob-16 sm:text-tab-16 lg:text-web-16 leading-[140%] text-[#5A5A5A] underline"
                              >
                                {category.name.replace(/&amp;/g, "&")}
                                {productCategoryLength === index + 1 ? "" : ","}
                              </li>
                            )
                          )
                        : ""} */}

                      {/* <li className="text-mob-16 sm:text-tab-16 lg:text-web-16 leading-[140%] text-[#5A5A5A] underline">
                  All Products,
                </li> */}
                      {/* </ul> */}
                      {/* </div> */}
                    </div>
                  </div>
                  {/* buttons */}
                  <div className="bg-PrimaryLightBlue flex  items-center gap-mob-20 sm:gap-tab-30 lg:gap-web-30 pt-mob-20 sm:pt-tab-25 lg:pt-web-30 pb-mob-20 sm:pb-tab-25 lg:pb-web-30 px-mob-23 sm:px-tab-23 lg:px-web-23">
                    <button
                      className="bg-SecondaryYellow  text-PrimaryLightBlue py-mob-12 sm:py-tab-12 lg:py-web-12 px-mob-16 sm:px-tab-24 lg:px-web-24 rounded-mob-6 sm:rounded-tab-6 lg:rounded-web-6 text-mob-19 sm:text-tab-22 lg:text-web-22 font-medium"
                      onClick={() =>
                        router.push(
                          `/details/${productDetails?.result?.slug}`
                        )
                      }
                    >
                      Show Details
                    </button>

                    {/* <button className="flex gap-mob-5 sm:gap-tab-5 lg:gap-web-5 text-mob-19 sm:text-tab-20 lg:text-web-20 items-center text-White">
            <div className="w-mob-24 sm:w-tab-24 lg:w-web-24">
              <Image
                src="/images/download.svg"
                alt="icon"
                fill
                className="!static !h-auto object-cover w-full object-center"
              />
            </div>
            Download Data Sheet
          </button> */}
                  </div>
                </>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
